import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { formatCurrency, getCurrency, convertCurrency } from './utils/currency';
import './HotelBookingThankYouPage.css';

const HotelBookingThankYouPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { bookingId, hotel, offer, guestInfo, searchParams, paymentDetails, amadeusBooking } = location.state || {};

  useEffect(() => {
    if (!bookingId || !hotel) {
      console.warn('[Thank You Page] Missing booking data, redirecting to hotels');
      navigate('/hotels');
      return;
    }

    // Log booking status
    console.log('[Thank You Page] ========== BOOKING CONFIRMATION ==========');
    console.log('[Thank You Page] Booking ID:', bookingId);
    console.log('[Thank You Page] Hotel:', hotel.name);
    console.log('[Thank You Page] Guest:', guestInfo?.firstName, guestInfo?.lastName);
    
    // Check Amadeus booking status
    if (amadeusBooking?.success) {
      console.log('[Thank You Page] ✅ BOOKING CREATED WITH AMADEUS');
      console.log('[Thank You Page] Amadeus Booking Reference:', amadeusBooking.bookingReference);
      console.log('[Thank You Page] Amadeus Status:', amadeusBooking.status);
      console.log('[Thank You Page] Amadeus Message:', amadeusBooking.message);
    } else {
      console.log('[Thank You Page] ⚠️ BOOKING SAVED LOCALLY (Not booked with Amadeus)');
      console.log('[Thank You Page] Amadeus Error:', amadeusBooking?.error || 'Not attempted');
      console.log('[Thank You Page] Note:', amadeusBooking?.note || 'Payment processed, booking saved locally');
    }
    console.log('[Thank You Page] Payment Details:', paymentDetails);
    console.log('[Thank You Page] ============================================');
  }, [bookingId, hotel, navigate, amadeusBooking, paymentDetails, guestInfo]);

  const formatPriceDisplay = (price) => {
    if (!price || !price.total) return 'Price not available';
    
    const userCurrency = getCurrency() || { code: 'NGN', symbol: '₦' };
    const apiCurrency = (price.currency || searchParams?.currency || 'NGN').toUpperCase();
    const apiAmount = parseFloat(price.total);
    
    if (isNaN(apiAmount) || apiAmount <= 0) {
      return 'Price not available';
    }
    
    if (apiCurrency === userCurrency.code) {
      return formatCurrency(apiAmount, { 
        decimals: 0, 
        currency: userCurrency 
      });
    }
    
    try {
      const convertedAmount = convertCurrency(apiAmount, apiCurrency, userCurrency.code);
      
      if (!convertedAmount || convertedAmount <= 0) {
        return `${apiCurrency} ${apiAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
      }
      
      return formatCurrency(convertedAmount, { 
        decimals: 0, 
        currency: userCurrency 
      });
    } catch (error) {
      return `${apiCurrency} ${apiAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const calculateNights = () => {
    if (!searchParams?.checkInDate || !searchParams?.checkOutDate) return 0;
    const checkIn = new Date(searchParams.checkInDate);
    const checkOut = new Date(searchParams.checkOutDate);
    const diffTime = Math.abs(checkOut - checkIn);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (!bookingId || !hotel) {
    return null;
  }

  const nights = calculateNights();

  return (
    <div className="hotel-thank-you-page">
      <Header />
      
      <div className="thank-you-container">
        <div className="success-animation">
          <div className="success-icon">
            <svg viewBox="0 0 100 100" className="checkmark">
              <circle cx="50" cy="50" r="45" fill="none" stroke="#EEC439" strokeWidth="4"/>
              <path d="M30 50 L45 65 L70 35" fill="none" stroke="#EEC439" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>

        <div className="thank-you-header">
          <h1>Booking Confirmed!</h1>
          <p className="success-message">
            Thank you, {guestInfo?.firstName || 'Guest'}! Your hotel reservation has been successfully completed.
          </p>
        </div>

        <div className="booking-confirmation-card">
          <div className="confirmation-header">
            <h2>Booking Confirmation</h2>
            <div className="booking-id-badge">
              <span className="id-label">Booking ID:</span>
              <span className="id-value">{bookingId}</span>
            </div>
          </div>

          <div className="confirmation-content">
            <div className="confirmation-section">
              <h3 className="section-heading">Hotel Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Hotel Name</span>
                  <span className="info-value">{hotel.name}</span>
                </div>
                {hotel.rating && (
                  <div className="info-item">
                    <span className="info-label">Rating</span>
                    <span className="info-value">
                      {'⭐'.repeat(Math.floor(hotel.rating))} {hotel.rating.toFixed(1)}/5
                    </span>
                  </div>
                )}
                {hotel.address && (
                  <div className="info-item full-width">
                    <span className="info-label">Address</span>
                    <span className="info-value">
                      {hotel.address.lines?.join(', ')}
                      {hotel.address.cityName && `, ${hotel.address.cityName}`}
                      {hotel.address.countryCode && `, ${hotel.address.countryCode}`}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="confirmation-section">
              <h3 className="section-heading">Room Details</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Room Type</span>
                  <span className="info-value">
                    {offer?.room?.type || offer?.room?.typeEstimated?.category || 'Standard Room'}
                  </span>
                </div>
                {offer?.room?.typeEstimated && (
                  <>
                    {offer.room.typeEstimated.beds && (
                      <div className="info-item">
                        <span className="info-label">Beds</span>
                        <span className="info-value">
                          {offer.room.typeEstimated.beds} {offer.room.typeEstimated.beds === 1 ? 'Bed' : 'Beds'}
                        </span>
                      </div>
                    )}
                    {offer.room.typeEstimated.bedType && (
                      <div className="info-item">
                        <span className="info-label">Bed Type</span>
                        <span className="info-value">{offer.room.typeEstimated.bedType}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="confirmation-section">
              <h3 className="section-heading">Stay Details</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Check-in</span>
                  <span className="info-value">
                    {searchParams?.checkInDate ? formatDate(searchParams.checkInDate) : 'N/A'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Check-out</span>
                  <span className="info-value">
                    {searchParams?.checkOutDate ? formatDate(searchParams.checkOutDate) : 'N/A'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Duration</span>
                  <span className="info-value">
                    {nights} {nights === 1 ? 'night' : 'nights'}
                  </span>
                </div>
              </div>
            </div>

            <div className="confirmation-section">
              <h3 className="section-heading">Guest Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Name</span>
                  <span className="info-value">
                    {guestInfo?.firstName} {guestInfo?.lastName}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Email</span>
                  <span className="info-value">{guestInfo?.email}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Phone</span>
                  <span className="info-value">{guestInfo?.phone}</span>
                </div>
                {guestInfo?.address && (
                  <div className="info-item full-width">
                    <span className="info-label">Address</span>
                    <span className="info-value">
                      {guestInfo.address}
                      {guestInfo.city && `, ${guestInfo.city}`}
                      {guestInfo.country && `, ${guestInfo.country}`}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="confirmation-section">
              <h3 className="section-heading">Payment Information</h3>
              <div className="payment-summary">
                <div className="payment-item">
                  <span className="payment-label">Total Amount Paid</span>
                  <span className="payment-amount">
                    {formatPriceDisplay(offer?.price || hotel?.price)}
                  </span>
                </div>
                {paymentDetails?.paymentMethod && (
                  <div className="payment-item">
                    <span className="payment-label">Payment Method</span>
                    <span className="payment-value">{paymentDetails.paymentMethod}</span>
                  </div>
                )}
                {paymentDetails?.transactionId && (
                  <div className="payment-item">
                    <span className="payment-label">Transaction ID</span>
                    <span className="payment-value">{paymentDetails.transactionId}</span>
                  </div>
                )}
                {paymentDetails?.paymentDate && (
                  <div className="payment-item">
                    <span className="payment-label">Payment Date</span>
                    <span className="payment-value">
                      {new Date(paymentDetails.paymentDate).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Amadeus Booking Status - Only show if successfully booked with Amadeus */}
            {amadeusBooking && amadeusBooking.success && (
              <div className="confirmation-section">
                <h3 className="section-heading">Booking Status</h3>
                <div className="payment-summary">
                  <div className="payment-item" style={{ background: '#e8f5e9', padding: '1rem', borderRadius: '8px', border: '1px solid #4caf50' }}>
                    <span className="payment-label" style={{ color: '#2e7d32', fontWeight: '600' }}>
                      ✅ Booked with Amadeus
                    </span>
                    <span className="payment-value" style={{ color: '#2e7d32' }}>
                      {amadeusBooking.status || 'CONFIRMED'}
                    </span>
                  </div>
                  {amadeusBooking.bookingReference && (
                    <div className="payment-item">
                      <span className="payment-label">Amadeus Booking Reference</span>
                      <span className="payment-value">{amadeusBooking.bookingReference}</span>
                    </div>
                  )}
                  {amadeusBooking.message && (
                    <div className="payment-item">
                      <span className="payment-label">Status Message</span>
                      <span className="payment-value">{amadeusBooking.message}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="next-steps-card">
          <h3 className="next-steps-title">What's Next?</h3>
          <ul className="next-steps-list">
            <li>
              <span className="step-icon">🏨</span>
              <span>Please arrive at the hotel on <strong>{searchParams?.checkInDate ? formatDate(searchParams.checkInDate) : 'your check-in date'}</strong></span>
            </li>
            <li>
              <span className="step-icon">💼</span>
              <span>Bring a valid ID for check-in at the hotel reception</span>
            </li>
          </ul>
        </div>

        <div className="action-buttons">
          <button 
            className="primary-button"
            onClick={() => window.print()}
          >
            Print Confirmation
          </button>
          <button 
            className="secondary-button"
            onClick={() => navigate('/dashboard')}
          >
            View My Bookings
          </button>
          <button 
            className="tertiary-button"
            onClick={() => navigate('/hotels')}
          >
            Book Another Hotel
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HotelBookingThankYouPage;
